import { useState } from 'react';

import type { TFunction } from 'i18next';

import type { IOrganizationUserDto } from '@treneo/models';
import { toast } from '@treneo/shared/ui';

import { getUserOptionLabel, isEmailEmpty, isValidEmail } from '../utils';

type UseInviteTrainerFormParams = {
  t: TFunction;
  organizationId: string;
  users: IOrganizationUserDto[];
  onInvite: (email: string) => Promise<void>;
  onSuccess: () => void;
};

export const useInviteTrainerForm = ({
  t,
  organizationId,
  users,
  onInvite,
  onSuccess
}: UseInviteTrainerFormParams) => {
  const [email, setEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<IOrganizationUserDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nonTrainerUsers = users.filter(user => !user.isTrainer);

  const getOptionLabel = (user: IOrganizationUserDto | string): string => {
    if (typeof user === 'string') {
      return user;
    }
    return getUserOptionLabel(user);
  };

  const getFinalEmail = (): string => {
    return selectedUser ? selectedUser.email : email.trim();
  };

  const validateEmail = (emailToValidate: string): boolean => {
    if (isEmailEmpty(emailToValidate)) {
      toast.error(t('trainers.emailRequired'));
      return false;
    }

    if (!isValidEmail(emailToValidate)) {
      toast.error(t('trainers.invalidEmail'));
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setEmail('');
    setSelectedUser(null);
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!organizationId) {
      return;
    }

    const finalEmail = getFinalEmail();

    if (!validateEmail(finalEmail)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onInvite(finalEmail);
      resetForm();
      toast.success(t('trainers.inviteSuccess'));
      onSuccess();
    } catch (error) {
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleInputChange = (newInputValue: string) => {
    setEmail(newInputValue);
    if (selectedUser) {
      setSelectedUser(null);
    }
  };

  const handleValueChange = (newValue: IOrganizationUserDto | string | null) => {
    if (typeof newValue === 'string') {
      setEmail(newValue);
      setSelectedUser(null);
    } else {
      setSelectedUser(newValue);
      const emailForSelectedUser = newValue ? newValue.email : '';
      setEmail(emailForSelectedUser);
    }
  };

  return {
    email,
    selectedUser,
    isSubmitting,
    nonTrainerUsers,
    getOptionLabel,
    handleSubmit,
    handleInputChange,
    handleValueChange,
    isDisabled: isEmailEmpty(email) && !selectedUser
  };
};
